// =============================================================================
// ADMIN THUNKS — Role Management
//
// Each thunk follows the exact same pattern used in dloThunks.ts:
//   1. createAsyncThunk with a typed return value and typed error (rejectValue)
//   2. createApiClient() — builds an Axios instance with the MSAL Bearer token
//      and period-week headers already attached by the request interceptor
//   3. try/catch — on success return the data; on failure call rejectWithValue()
//      so the slice's .rejected case receives a plain string (not a serialised
//      Error object, which RTK would strip anyway)
//
// WHY rejectWithValue instead of throw?
//   RTK's createAsyncThunk catches thrown errors and converts them to a
//   SerializedError — losing our custom message. rejectWithValue() lets us send
//   a clean, human-readable string straight to the slice's rejected handler.
// =============================================================================

import { createAsyncThunk } from '@reduxjs/toolkit';
import { createApiClient } from '../../api/apiClient';
import { Role, RoleAvailability, RoleFormValues, RoleLocationType, RoleStatus } from '../../screens/Admin/pages/RoleManagement/types';

// =============================================================================
// RESPONSE NORMALIZATION HELPER
// Maps the POST /roles and PUT /roles/:name response shape back to a Role object
// so the slice can work with a consistent type regardless of which modules were
// included in availableIn.
// =============================================================================

function normalizeRoleResponse(responseData: any, formValues: RoleFormValues): Role {
  const data = responseData?.data ?? {};
  const lo  = data.laborOptimization;
  const obs = data.observations;

  const availableIn: RoleAvailability[] = [];
  if (lo)  availableIn.push('Labor Optimization');
  if (obs) availableIn.push('Observations');

  const roleName   = lo?.roleName ?? obs?.roleName ?? formValues.name;
  const locTypes =
    lo?.locationTypes ??
    obs?.locationTypes ??
    (typeof lo?.locationType === 'string' ? lo.locationType.split(',').map((v: string) => v.trim()).filter(Boolean) : undefined) ??
    (typeof obs?.locationType === 'string' ? obs.locationType.split(',').map((v: string) => v.trim()).filter(Boolean) : undefined) ??
    formValues.locTypes ?? [];
  const statusBool = lo?.status ?? obs?.status ?? true;

  return {
    id:          lo?.vpZone ?? (obs?.roleId != null ? String(obs.roleId) : undefined) ?? roleName,
    name:        roleName,
    availableIn: availableIn.length > 0 ? availableIn : (formValues.availableIn as RoleAvailability[]),
    locTypes:    locTypes as RoleLocationType[],
    status:      statusBool ? 'Enabled' : 'Disabled',
    organizationTypes: lo?.organizationTypes ?? obs?.organizationTypes ?? formValues.organizationTypes,
    threePlStatus: lo?.threePlStatus ?? obs?.threePlStatus ?? formValues.threePlStatus,
    roleType: lo?.roleType ?? obs?.roleType ?? formValues.roleType,
    createdAt:   lo?.createdAt  ?? obs?.createdAt,
    updatedAt:   lo?.modifiedAt ?? obs?.modifiedAt,
  };
}

export interface FetchRolesParams {
  page?: number;
  size?: number;
  sortBy?: 'roleName' | 'name' | 'modifiedAt';
  sortDirection?: 'asc' | 'desc';
  roleName?: string;
  statuses?: string;
  availableIns?: string;
  locationTypes?: string;
  organizationTypes?: string;
  threePlStatuses?: string;
  roleTypes?: string;

  // Backward-compatible aliases
  search?: string;
  status?: string;
  availableIn?: string;
  locationType?: string;
  organizationType?: string;
  threePlStatus?: string;
  roleType?: string;
  locTypes?: string;
}

// =============================================================================
// fetchRoles — GET /admin/role-management/roles
// Called on component mount via useRoleManagementData (optionally with params).
// Also called with filter/sort/pagination params when filters are applied or
// user navigates pages / clicks column headers.
// Returns paginated response (content + metadata) which replaces state.adminRoles.roles.
// =============================================================================

export const fetchRoles = createAsyncThunk<
  any,              // fulfilled payload — paginated response or legacy array
  FetchRolesParams | void,
  { rejectValue: string }
>(
  'adminRoles/fetchRoles',
  async (params, { rejectWithValue }) => {
    try {
      const api = createApiClient();
      const queryParams: Record<string, any> = {};

      // Pagination
      if (params?.page !== undefined) queryParams.page = params.page;
      if (params?.size !== undefined) queryParams.size = params.size;

      // Sorting
      if (params?.sortBy) queryParams.sortBy = params.sortBy;
      if (params?.sortDirection) queryParams.sortDirection = params.sortDirection;

      // Filtering
      if (params?.roleName) queryParams.roleName = params.roleName;
      else if (params?.search) queryParams.roleName = params.search;

      if (params?.statuses) queryParams.statuses = params.statuses;
      else if (params?.status) queryParams.statuses = params.status;

      if (params?.availableIns) queryParams.availableIns = params.availableIns;
      else if (params?.availableIn) queryParams.availableIns = params.availableIn;

      if (params?.locationTypes) queryParams.locationTypes = params.locationTypes;
      else if (params?.locationType) queryParams.locationTypes = params.locationType;
      else if (params?.locTypes) queryParams.locationTypes = params.locTypes;

      if (params?.organizationTypes) queryParams.organizationTypes = params.organizationTypes;
      else if (params?.organizationType) queryParams.organizationTypes = params.organizationType;

      if (params?.threePlStatuses) queryParams.threePlStatuses = params.threePlStatuses;
      else if (params?.threePlStatus) queryParams.threePlStatuses = params.threePlStatus;

      if (params?.roleTypes) queryParams.roleTypes = params.roleTypes;
      else if (params?.roleType) queryParams.roleTypes = params.roleType;

      const response = await api.get('/admin/role-management/roles', {
        params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
        _skipLocationContext: true,
      } as any);
      return response.data;
    } catch (error: any) {
      console.error('[AdminThunks] fetchRoles error:', error);
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? 'Failed to load roles'
      );
    }
  }
);

// =============================================================================
// createRole — POST /admin/roles
// Called when the user submits the Add Role form.
// Sends RoleFormValues (no id / timestamps — those are server-assigned).
// Returns the newly created Role (with server-assigned id) so the slice
// can push it into roles[] without a separate refetch.
// =============================================================================

export const createRole = createAsyncThunk<
  Role,              // fulfilled payload — normalized role shape
  RoleFormValues,    // argument — form values collected by RoleFormModal
  { rejectValue: string }
>(
  'adminRoles/createRole',
  async (formValues, { rejectWithValue }) => {
    try {
      const api = createApiClient();
      const payload: Record<string, any> = {
        roleName:    formValues.name,
        availableIn: formValues.availableIn,
        status:      formValues.status === 'Enabled',
        locationTypes: formValues.locTypes,
        organizationTypes: formValues.organizationTypes,
        threePlStatus: formValues.threePlStatus,
      };
      if (formValues.roleType) {
        payload.roleType = formValues.roleType;
      }
      if (['DSD', 'NON-DSD'].includes((formValues.roleType ?? '').toUpperCase()) && formValues.vpZones?.length) {
        payload.vpZones = formValues.vpZones;
      }
      const response = await api.post('/admin/role-management/roles', payload);
      return normalizeRoleResponse(response.data, formValues);
    } catch (error: any) {
      console.error('[AdminThunks] createRole error:', error);
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? 'Failed to create role'
      );
    }
  }
);

// =============================================================================
// updateRole — PUT /admin/role-management/roles/{id}
// Called when the user submits the Edit Role form.
// `roleId` is the server-generated integer ID used as the URL path param.
// The body may contain a different `roleName` when the user renames the role.
// Returns the updated Role (normalized) so the slice and UI reflect the new state.
// =============================================================================

export const updateRole = createAsyncThunk<
  Role,
  { roleId: string; updates: RoleFormValues },  // roleId = path param
  { rejectValue: string }
>(
  'adminRoles/updateRole',
  async ({ roleId, updates }, { rejectWithValue }) => {
    try {
      const api = createApiClient();
      const payload: Record<string, any> = {
        roleName:    updates.name,
        availableIn: updates.availableIn,
        status:      updates.status === 'Enabled',
        organizationTypes: updates.organizationTypes,
        threePlStatus: updates.threePlStatus,
      };
      if (updates.locTypes?.length) {
        // Optional on update: omitted when empty to preserve existing server value.
        payload.locationTypes = updates.locTypes;
      }
      if (updates.roleType) {
        payload.roleType = updates.roleType;
      }
      if (['DSD', 'NON-DSD'].includes((updates.roleType ?? '').toUpperCase()) && updates.vpZones?.length) {
        payload.vpZones = updates.vpZones;
      }
      const response = await api.put(
        `/admin/role-management/roles/${encodeURIComponent(roleId)}`,
        payload
      );
      return normalizeRoleResponse(response.data, updates);
    } catch (error: any) {
      console.error('[AdminThunks] updateRole error:', error);
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? 'Failed to update role'
      );
    }
  }
);

// =============================================================================
// toggleRoleStatus — PATCH /admin/role-management/roles/status
// Called when the user flips the status toggle in the table row.
// API contract requires roleName + boolean status + availableIn array.
// We normalize the API response into Role shape and fall back to optimistic
// local role update if the response body is empty.
// =============================================================================

export const toggleRoleStatus = createAsyncThunk<
  Role,
  { role: Role; status: boolean },
  { rejectValue: string }
>(
  'adminRoles/toggleRoleStatus',
  async ({ role, status }, { rejectWithValue }) => {
    try {
      const api = createApiClient();
      const response = await api.patch('/admin/role-management/roles/status', {
        roleName: role.name,
        status,
        availableIn: role.availableIn,
      });

      const data = response?.data;
      if (data && typeof data === 'object') {
        return {
          ...role,
          ...data,
          name: data.roleName ?? data.name ?? role.name,
          status: typeof data.status === 'boolean'
            ? (data.status ? 'Enabled' : 'Disabled')
            : (data.status ?? (status ? 'Enabled' : 'Disabled')),
          availableIn: data.availableIn ?? role.availableIn,
          locTypes: data.locTypes ?? role.locTypes,
        } as Role;
      }

      return {
        ...role,
        status: status ? 'Enabled' : 'Disabled',
      };
    } catch (error: any) {
      console.error('[AdminThunks] toggleRoleStatus error:', error);
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? 'Failed to update role status'
      );
    }
  }
);

// =============================================================================
// deleteRole — DELETE /admin/role-management/fixed-hour-roles/{vpZone}
// Called when the user confirms the delete action in the confirmation dialog.
// Returns the vpZone string so the slice can remove the matching row from state.
// =============================================================================

export const deleteRole = createAsyncThunk<
  string,   // fulfilled payload — the vpZone (role id) that was deleted
  string,   // argument — vpZone / role name to delete (always uppercase)
  { rejectValue: string }
>(
  'adminRoles/deleteRole',
  async (vpZone, { rejectWithValue }) => {
    try {
      const api = createApiClient();
      await api.delete(`/admin/role-management/fixed-hour-roles/${encodeURIComponent(vpZone)}`);
      return vpZone;
    } catch (error: any) {
      console.error('[AdminThunks] deleteRole error:', error);
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? 'Failed to delete role'
      );
    }
  }
);

// =============================================================================
// fetchLocationTypes — GET /admin/role-management/location-types
// Called when the Add Role form opens to populate the Location Type dropdown.
// Falls back to hardcoded values in the form if the call fails.
// =============================================================================

export const fetchLocationTypes = createAsyncThunk<
  string[],   // fulfilled payload — array of location type strings
  void,
  { rejectValue: string }
>(
  'adminRoles/fetchLocationTypes',
  async (_, { rejectWithValue }) => {
    try {
      const api = createApiClient();
      const response = await api.get('/admin/role-management/location-types');
      // Response shape: { data: string[], totalCount: number }
      return response.data?.data ?? response.data ?? [];
    } catch (error: any) {
      console.error('[AdminThunks] fetchLocationTypes error:', error);
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? 'Failed to load location types'
      );
    }
  }
);

// =============================================================================
// fetchRoleStats — GET /admin/role-management/roles (no pagination)
// Called on AdminDashboard mount to fetch role statistics for the dashboard tile.
// Returns counts of total, enabled, and disabled roles (no pagination applied).
// =============================================================================

export interface RoleStatsResponse {
  totalCount: number;
  enabledCount: number;
  disabledCount: number;
}

export const fetchRoleStats = createAsyncThunk<
  RoleStatsResponse,
  void,
  { rejectValue: string }
>(
  'adminRoleStats/fetchRoleStats',
  async (_, { rejectWithValue }) => {
    try {
      const api = createApiClient();
      // Fetch without pagination to get ALL roles for counting
      const response = await api.get('/admin/role-management/roles', {
        params: {
          page: 0,
          size: 1000, // Large size to get all roles in one request
        },
        _skipLocationContext: true,
      } as any);
      
      // Handle both array and paginated response formats
      const data = response.data;
      const content: any[] = Array.isArray(data) ? data : (data?.content ?? []);
      const totalElements: number = Array.isArray(data) ? data.length : (data?.totalElements ?? content.length);

      // Normalize status: API may return boolean (true/false) or string ('Enabled'/'Disabled')
      const normalizeStatus = (status: any): string =>
        typeof status === 'boolean' ? (status ? 'Enabled' : 'Disabled') : (status ?? 'Enabled');
      const enabledCount = content.filter((role: any) => normalizeStatus(role.status) === 'Enabled').length;
      const disabledCount = content.filter((role: any) => normalizeStatus(role.status) === 'Disabled').length;
      
      return {
        totalCount: totalElements,
        enabledCount,
        disabledCount,
      };
    } catch (error: any) {
      console.error('[AdminThunks] fetchRoleStats error:', error);
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? 'Failed to fetch role statistics'
      );
    }
  }
);
